import React, { useState, useMemo } from "react";
import { MANIFESTS, compatibilityCount } from "./public/manifest";

const CATEGORIES = [
  { key: "all",       label: "all" },
  { key: "wall",      label: "walls" },
  { key: "carousel",  label: "carousels" },
  { key: "hero",      label: "heroes" },
  { key: "compact",   label: "compact" },
  { key: "stat",      label: "stats" },
  { key: "logo",      label: "logos" },
  { key: "floating",  label: "floating" },
  { key: "inline",    label: "inline" },
  { key: "marquee",   label: "marquees" },
];

const EditKind = ({ kind, onUpdate, proofs = [], proofsLoading = false }) => {
  const [filter, setFilter] = useState("all");

  const visibleCategories = useMemo(() => {
    const used = new Set(MANIFESTS.map((m) => m.category).filter(Boolean));
    return CATEGORIES.filter((c) => c.key === "all" || used.has(c.key));
  }, []);

  const filtered = useMemo(() => {
    if (filter === "all") return MANIFESTS;
    return MANIFESTS.filter((m) => m.category === filter);
  }, [filter]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-1.5">
        {visibleCategories.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setFilter(c.key)}
            className={`px-2.5 py-1 text-xs font-bold uppercase tracking-wider border-[2px] border-black transition-colors ${filter === c.key ? "bg-black text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {filtered.map((manifest) => (
          <Card
            key={manifest.key}
            manifest={manifest}
            selected={kind === manifest.key}
            onClick={() => onUpdate("kind", manifest.key)}
            counts={compatibilityCount(manifest, proofs)}
            loading={proofsLoading}
          />
        ))}
      </div>
    </div>
  );
};

const Card = ({ manifest, selected, onClick, counts, loading = false }) => {
  const compatible = counts.compatible;
  const total = counts.total;
  // While the proofs are still in flight every card would read "no proofs
  // yet", which is a confident answer we don't have. Stay neutral instead.
  const isInsufficient = !loading && compatible < (manifest.minProofs || 1);

  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative text-left bg-white border-[2px] overflow-hidden transition-all hover:-translate-y-0.5 hover:shadow-[4px_4px_0_rgba(0,0,0,0.25)] ${selected ? "border-ssblue shadow-[4px_4px_0_rgba(62,141,255,0.4)]" : "border-black shadow-[2px_2px_0_rgba(0,0,0,0.18)]"}`}
    >
      <div className="p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="font-black text-sm text-slate-900 truncate">{manifest.name}</div>
          {selected && (
            <span className="flex-none text-[10px] font-black uppercase tracking-wider bg-ssblue text-white px-1.5 py-0.5 rounded">
              picked
            </span>
          )}
        </div>
        <p className="mt-1 text-[11px] text-slate-600 leading-snug line-clamp-2">{manifest.description}</p>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${loading ? "bg-slate-100 text-slate-400" : isInsufficient ? "bg-ssred/15 text-ssred" : "bg-ssgreen/15 text-ssgreen"}`}>
            {loading
              ? "counting…"
              : total === 0
              ? "no proofs yet"
              : compatible === 0
              ? "no compatible proofs"
              : `${compatible} compatible proof${compatible === 1 ? "" : "s"}`}
          </span>
          <span className="text-[10px] text-slate-400 font-bold">
            kinds: {manifest.kinds.join(", ")}
          </span>
          <span
            className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 ${manifest.supportsInfiniteScroll ? "bg-ssblue/15 text-ssblue" : "bg-slate-100 text-slate-500"}`}
            title={manifest.supportsInfiniteScroll
              ? "Loads more proofs as the visitor scrolls."
              : `Shows up to ${manifest.maxProofs || "a fixed number of"} proofs, with no scroll loading.`}
          >
            {manifest.supportsInfiniteScroll ? "∞ infinite scroll" : "fixed set"}
          </span>
        </div>
      </div>
    </button>
  );
};

export default EditKind;
